import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Users, Plus, Pencil, Trash2, X, Save, Loader2, Mail, Phone, Linkedin, Play, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "./ImageUploader";

const emptyMember = { first_name: "", last_name: "", display_last_name: false, role: "", bio: "", avatar_url: "", avatar_video_url: "", avatar_video_has_audio: false, video_url: "", video_poster: "", email: "", phone: "", linkedin_url: "", sort_order: 0, active: true };

export default function TeamAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/admin/team")).data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit.first_name) return toast.error("Prénom obligatoire");
    try {
      if (edit.id) await api.put(`/admin/team/${edit.id}`, edit);
      else await api.post("/admin/team", edit);
      toast.success("Enregistré"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce membre ?")) return;
    await api.delete(`/admin/team/${id}`); load();
  };
  const move = async (m, dir) => {
    const idx = items.findIndex(x => x.id === m.id);
    const j = idx + dir; if (j < 0 || j >= items.length) return;
    const swapped = items[j];
    await Promise.all([
      api.put(`/admin/team/${m.id}`, { ...m, sort_order: swapped.sort_order }),
      api.put(`/admin/team/${swapped.id}`, { ...swapped, sort_order: m.sort_order }),
    ]);
    load();
  };

  return (
    <div className="p-6 md:p-10 space-y-5" data-testid="admin-team-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-2">Contenu</div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Équipe</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Présentez votre équipe sur la page publique <code>/equipe</code>. Photo, fonction, bio, vidéo, coordonnées directes (facultatives).
          </p>
        </div>
        <Button onClick={() => setEdit({ ...emptyMember, sort_order: items.length })} className="bg-[color:var(--brand-red)]" data-testid="new-team-member-btn">
          <Plus className="w-4 h-4 mr-1" />Nouveau membre
        </Button>
      </header>

      {loading ? (
        <Card className="p-14 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></Card>
      ) : items.length === 0 ? (
        <Card className="p-14 text-center border-dashed">
          <Users className="w-10 h-10 text-[hsl(var(--muted-foreground))]/30 mx-auto mb-3" />
          <div className="font-display font-bold text-[color:var(--brand-navy)]">Aucun membre d'équipe</div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Ajoutez les personnes à mettre en avant sur votre page équipe.</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((m, i) => (
            <Card key={m.id} className={`p-4 border ${!m.active ? "opacity-60" : ""}`} data-testid={`team-card-${m.id}`}>
              <div className="flex items-start gap-3 mb-3">
                {m.avatar_url ? (
                  <img src={m.avatar_url} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-[color:var(--brand-sky)]/30" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[color:var(--brand-sky)]/10 flex items-center justify-center text-xl font-bold text-[color:var(--brand-sky)]">
                    {(m.first_name[0] || "?") + (m.display_last_name && m.last_name ? m.last_name[0] : "")}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[color:var(--brand-navy)] truncate">{m.first_name}{m.display_last_name && m.last_name && ` ${m.last_name}`}</div>
                  <div className="text-xs text-[color:var(--brand-sky)] font-semibold truncate">{m.role || "—"}</div>
                  <div className="flex items-center gap-1.5 mt-1 text-[10px] text-[hsl(var(--muted-foreground))]">
                    {m.email && <Mail className="w-3 h-3" />}{m.phone && <Phone className="w-3 h-3" />}{m.video_url && <Play className="w-3 h-3" />}{m.linkedin_url && <Linkedin className="w-3 h-3" />}
                    {!m.active && <span className="uppercase font-bold text-amber-700">Inactif</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-0.5 shrink-0">
                  <button type="button" onClick={() => move(m, -1)} disabled={i === 0} className="w-6 h-5 rounded hover:bg-[hsl(var(--muted))] disabled:opacity-30 flex items-center justify-center"><ChevronUp className="w-3 h-3" /></button>
                  <button type="button" onClick={() => move(m, 1)} disabled={i === items.length - 1} className="w-6 h-5 rounded hover:bg-[hsl(var(--muted))] disabled:opacity-30 flex items-center justify-center"><ChevronDown className="w-3 h-3" /></button>
                </div>
              </div>
              {m.bio && <p className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2">{m.bio}</p>}
              <div className="flex gap-1.5 mt-3">
                <Button variant="outline" size="sm" onClick={() => setEdit(m)}><Pencil className="w-3 h-3 mr-1" />Éditer</Button>
                <Button variant="outline" size="sm" onClick={() => del(m.id)} className="text-red-600"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()} data-testid="team-edit-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-display font-bold text-xl flex items-center gap-2"><Users className="w-5 h-5" />{edit.id ? "Éditer membre" : "Nouveau membre"}</h3>
              <button onClick={() => setEdit(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Photo */}
              <div>
                <Label>Photo / avatar *</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  {edit.avatar_url ? (
                    <img src={edit.avatar_url} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-[color:var(--brand-sky)]/30" />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-[color:var(--brand-sky)]/10 flex items-center justify-center text-[color:var(--brand-sky)] font-bold text-3xl">
                      {(edit.first_name[0] || "?") + (edit.display_last_name && edit.last_name ? edit.last_name[0] : "")}
                    </div>
                  )}
                  <div className="flex-1 space-y-2">
                    <ImageUploader folder="team" label="Uploader une photo" onUploaded={(url) => setEdit({ ...edit, avatar_url: url })} />
                    {edit.avatar_url && <button type="button" onClick={() => setEdit({ ...edit, avatar_url: "" })} className="text-xs text-red-600 hover:underline">Retirer</button>}
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Prénom *</Label><Input value={edit.first_name} onChange={e => setEdit({ ...edit, first_name: e.target.value })} data-testid="team-first-name" /></div>
                <div><Label>Nom (facultatif)</Label><Input value={edit.last_name} onChange={e => setEdit({ ...edit, last_name: e.target.value })} /></div>
                <div className="flex items-center gap-2 md:col-span-2"><Switch checked={edit.display_last_name} onCheckedChange={v => setEdit({ ...edit, display_last_name: v })} /><span className="text-sm">Afficher le nom publiquement (sinon : prénom seul)</span></div>
              </div>
              <div><Label>Fonction</Label><Input value={edit.role} onChange={e => setEdit({ ...edit, role: e.target.value })} placeholder="ex: Cheffe de projet · Responsable SAV…" /></div>
              <div><Label>Texte de présentation</Label><Textarea rows={4} value={edit.bio} onChange={e => setEdit({ ...edit, bio: e.target.value })} placeholder="Parcours, passions, spécialités…" /></div>

              {/* Avatar vidéo rond (Phase 14) */}
              <div className="p-3 rounded-xl border-2 border-[color:var(--brand-sky)]/40 bg-[color:var(--brand-sky)]/[0.04] space-y-2" data-testid="avatar-video-section">
                <Label className="flex items-center gap-1.5"><Play className="w-3.5 h-3.5" />Avatar vidéo rond (MP4 court, autoplay muet sur la fiche)</Label>
                <Input value={edit.avatar_video_url || ""} onChange={e => setEdit({ ...edit, avatar_video_url: e.target.value })} placeholder="URL d'un MP4 court (5-10s, boucle, vidéo au format portrait)" data-testid="avatar-video-url" />
                <div className="flex items-center gap-2">
                  <Switch checked={!!edit.avatar_video_has_audio} onCheckedChange={v => setEdit({ ...edit, avatar_video_has_audio: v })} data-testid="avatar-video-audio" />
                  <Label className="text-xs">La vidéo contient de l'audio (affichera un bouton toggle son)</Label>
                </div>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))]">S'affiche en cercle (comme un avatar photo). Recommandé : MP4 H.264, 512×512 ou 720×720, ≤ 2 Mo. L'audio est toujours désactivé au chargement pour respecter les politiques navigateur.</p>
              </div>

              {/* Video */}
              <div className="p-3 rounded-xl bg-[color:var(--brand-sky)]/5 border border-[color:var(--brand-sky)]/20 space-y-2">
                <Label className="flex items-center gap-1.5"><Play className="w-3.5 h-3.5" />Vidéo de présentation longue (facultatif)</Label>
                <Input value={edit.video_url} onChange={e => setEdit({ ...edit, video_url: e.target.value })} placeholder="MP4 direct ou YouTube/Vimeo embed" />
                <div className="flex items-center gap-2">
                  <ImageUploader folder="team-video" label="Poster vidéo" onUploaded={(url) => setEdit({ ...edit, video_poster: url })} />
                  <Input value={edit.video_poster} onChange={e => setEdit({ ...edit, video_poster: e.target.value })} className="flex-1 text-xs" placeholder="URL image poster" />
                </div>
              </div>

              {/* Contact */}
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="flex items-center gap-1"><Mail className="w-3 h-3" />Email direct (facultatif)</Label><Input value={edit.email} onChange={e => setEdit({ ...edit, email: e.target.value })} type="email" /></div>
                <div><Label className="flex items-center gap-1"><Phone className="w-3 h-3" />Téléphone direct (facultatif)</Label><Input value={edit.phone} onChange={e => setEdit({ ...edit, phone: e.target.value })} /></div>
                <div className="md:col-span-2"><Label className="flex items-center gap-1"><Linkedin className="w-3 h-3" />Lien LinkedIn (facultatif)</Label><Input value={edit.linkedin_url} onChange={e => setEdit({ ...edit, linkedin_url: e.target.value })} placeholder="https://www.linkedin.com/in/…" /></div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Ordre d'affichage</Label><Input type="number" value={edit.sort_order} onChange={e => setEdit({ ...edit, sort_order: +e.target.value })} /></div>
                <div className="flex items-center gap-2"><Switch checked={edit.active} onCheckedChange={v => setEdit({ ...edit, active: v })} /><Label>Actif (visible publiquement)</Label></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t">
              <Button variant="outline" onClick={() => setEdit(null)}>Annuler</Button>
              <Button onClick={save} className="bg-[color:var(--brand-navy)]" data-testid="save-team-btn"><Save className="w-4 h-4 mr-1" />Enregistrer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
